import { useState } from 'react';
import { closeModal } from 'actions/modal';
import { useAccessOrgs, useOrg, useReloadMeOnMount, useThunkDispatch } from 'utils/reduxHook';
import Typography from 'components/atoms/Typography';
import Icon from 'components/atoms/Icon';
import Card from 'components/atoms/Card';
import { handleSwitchOrg } from 'actions/auth';
import { isFunction } from 'utils/common';
import Button from 'components/atoms/Button';
import InAppRoutes, { pushProgressive } from 'utils/router';
import ModalWrapper, { ModalFooter } from './ModalWrapper';

interface SwitchOrganizationsProps {
  onComplete?: () => void;
  isCreate?: boolean;
}

function SwitchOrganizations(props: SwitchOrganizationsProps) {
  const dispatch = useThunkDispatch();
  const org = useOrg();
  const accessOrgs = useAccessOrgs();
  const [selectedNewOrgId, setSelectedNewOrgId] = useState<number>(org?.id || accessOrgs[0]?.id);

  useReloadMeOnMount();

  const handleSwitchOrgs = async () => {
    await dispatch(handleSwitchOrg(selectedNewOrgId));
    if (isFunction(props.onComplete)) props.onComplete();
    dispatch(closeModal());
  };

  const handleCreateOrg = () => {
    dispatch(closeModal());
    pushProgressive(InAppRoutes.CreateAccount);
  };

  return (
    <ModalWrapper>
      <Typography variant="h1" className="my-4">
        Your Accounts
      </Typography>
      {props.isCreate && (
        <Typography variant="base" className="my-4">
          Use one of these accounts instead of creating a new one.
        </Typography>
      )}
      <div className="border-t border-blueLightest pt-4 overflow-y-auto" style={{ maxHeight: 250 }}>
        {accessOrgs?.map((accessOrg) => (
          <Card
            elevation={2}
            key={accessOrg.id}
            rounded="md"
            className="bg-blueLightest py-6 px-8 flex flex-row items-center cursor-pointer mt-4 hover:bg-blueLighter transition-all duration-50"
            onClick={() => setSelectedNewOrgId(accessOrg.id)}
          >
            <div className="flex flex-col flex-1">
              <Typography variant="h3" className="text-blue">
                {accessOrg.name}
              </Typography>

              {accessOrg.status && accessOrg.status !== 'active' && (
                <Typography variant="base" className="text-blue text-sm italic">
                  ({accessOrg.status})
                </Typography>
              )}
            </div>
            {((!selectedNewOrgId && accessOrg.id === org.id) || selectedNewOrgId === accessOrg.id) && (
              <Icon name="check" size={22} className="text-green" />
            )}
          </Card>
        ))}
      </div>

      <div className="mt-12">
        <ModalFooter
          primary={{
            text: 'Continue with this Account',
            disabled: !selectedNewOrgId,
          }}
          hideSecondary
          moreButtons={
            props.isCreate && (
              <Button
                dataDurableId="createOrgButton"
                text="Create a new Account"
                type="tertiary"
                onClick={handleCreateOrg}
                className="mr-2"
              />
            )
          }
          onConfirm={handleSwitchOrgs}
        />
      </div>
    </ModalWrapper>
  );
}

export default SwitchOrganizations;
